/**
 * GateNotReady.jsx
 * =================
 * The "not ready yet" screen inside the MiniMatcher popup. It renders
 * when a refinement question option carried `gate_fail: true` and
 * the popup switches from step="questions" to step="gate".
 *
 * Purpose: some professional service categories (starting with
 * Private Certifier) can only help a customer AFTER a prior step has
 * been completed (e.g. lodging a Planning Consent application,
 * or preparing all consent documents). Rather than send that customer
 * a shortlist of businesses who will simply reject them, we tell
 * them the truth and route them to the matcher(s) that can help
 * them get ready.
 *
 * The rendered content varies by `gate.reason`. Currently supported:
 *   - "no_application" : user has not lodged a Planning Consent
 *   - "no_documents"   : user is missing engineering/plans/energy docs
 *
 * The educational cards deep-link into the terminology + pillar
 * pages already in the SEO footprint (build the web of internal
 * content). The handoff buttons emit the same
 * `buildpilot:open-matcher` event the rest of the platform
 * uses, so opening the Town Planner or Building Designer matcher
 * is one click and stays inside the popup workflow.
 *
 * Copy is deliberately neutral, non-alarming, and factors-based
 * (matching the 2026 legal / editorial guardrails: no timelines
 * quoted, no "watch out for" language, no cost figures).
 * No em-dashes.
 */
import React from 'react';
import { ArrowRight, ExternalLink, AlertCircle } from 'lucide-react';

const STATE_AUTHORITY = {
  SA: { label: 'PlanSA', url: 'https://plan.sa.gov.au' },
  VIC: { label: 'VBA', url: 'https://www.vba.vic.gov.au' },
  NSW: { label: 'NSW Planning Portal', url: 'https://www.planningportal.nsw.gov.au' },
  QLD: { label: 'QLD Business (Building certification)', url: 'https://www.business.qld.gov.au/industries/building-property-development' },
  WA: { label: 'WA Building Commission', url: 'https://www.commerce.wa.gov.au/building-commission' },
  TAS: { label: 'CBOS Tasmania', url: 'https://www.cbos.tas.gov.au' },
  ACT: { label: 'ACT Access Canberra', url: 'https://www.accesscanberra.act.gov.au' },
  NT: { label: 'NT Building Regulation', url: 'https://nt.gov.au/property/building' },
};

const REASON_COPY = {
  no_application: {
    heading: 'A private certifier can help once your application is lodged',
    body: 'Private certifiers assess a project against the National Construction Code and issue Building Rules Consent. That work only starts after your Planning Consent application has been lodged with the relevant authority. If you have not lodged yet, a town planner is the right first step, then come back here and a certifier can pick it up from there.',
  },
  no_documents: {
    heading: 'A private certifier will need your consent documents to start',
    body: 'To certify against the National Construction Code, a certifier reviews the full set of consent documents that describe your build: engineered slab and structural drawings, architectural plans, energy efficiency (NatHERS/BASIX) report, site plans, and any specialist reports. If any of those are missing, a building designer or the relevant specialist can help you get them together first.',
  },
};

// Recommended NEXT matcher categories per gate reason. Users can jump
// straight into that matcher from this screen and skip the "close +
// reopen with a different category" round trip.
const REASON_HANDOFFS = {
  no_application: [
    { category: 'professional:town_planner',      label: 'Town Planner',      sub: 'Prepares and lodges your Planning Consent application' },
    { category: 'professional:building_designer', label: 'Building Designer', sub: 'Prepares the design and drawings your planner needs' },
  ],
  no_documents: [
    { category: 'professional:building_designer', label: 'Building Designer',    sub: 'Coordinates architectural plans + package' },
    { category: 'professional:structural_engineer', label: 'Structural Engineer', sub: 'Slab, footings and frame engineering documents' },
    { category: 'professional:energy_assessor',   label: 'Energy Assessor',      sub: 'NatHERS / BASIX energy efficiency report' },
  ],
};

// Educational cross-links. Uses pillar + terminology pages already
// in the site's internal-linking web so this screen contributes to
// SEO topical authority, not just user support.
const EDU_LINKS = [
  { to: '/terminology/building-approval', label: 'Building approval in SA: Planning Consent + BRC explained' },
  { to: '/building-contracts-adelaide-guide', label: 'Building contracts glossary (SA, 2026)' },
  { to: '/terminology/nathers', label: 'What NatHERS star ratings actually measure' },
];

export default function GateNotReady({ gate, onOpenMatcher, onClose }) {
  const copy = REASON_COPY[gate.reason] || {
    heading: 'You are not quite ready for this service yet',
    body: 'We could not find enough context to match you with a certifier just yet. Complete the earlier steps first and come back here.',
  };
  const handoffs = REASON_HANDOFFS[gate.reason] || [];
  const stateAuth = gate.state ? STATE_AUTHORITY[String(gate.state).toUpperCase()] : null;

  return (
    <div className="bp-mm-gate" data-testid="mm-gate-not-ready" data-gate-reason={gate.reason}>
      <div className="bp-mm-gate-head" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
        <AlertCircle size={20} style={{ color: '#f5a623', flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'inherit', lineHeight: 1.35 }}>
            {copy.heading}
          </h3>
        </div>
      </div>

      <p className="bp-mm-gate-body" style={{ fontSize: 13.5, lineHeight: 1.6, opacity: 0.9, marginBottom: 16 }}>
        {copy.body}
      </p>

      {handoffs.length > 0 && (
        <div className="bp-mm-gate-handoffs" style={{ marginBottom: 18 }}>
          <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.7, marginBottom: 8 }}>
            Start with one of these first
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            {handoffs.map((h) => (
              <button
                key={h.category}
                type="button"
                className="bp-mm-gate-handoff"
                onClick={() => onOpenMatcher(h.category)}
                data-testid={`mm-gate-handoff-${h.category.replace(':', '-')}`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, padding: '12px 14px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
                  color: 'inherit', textAlign: 'left', cursor: 'pointer',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{h.label}</div>
                  <div style={{ fontSize: 12, opacity: 0.72, marginTop: 2 }}>{h.sub}</div>
                </div>
                <ArrowRight size={16} style={{ flexShrink: 0, opacity: 0.7 }} aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bp-mm-gate-edu" style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.7, marginBottom: 8 }}>
          Read up while you get ready
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
          {EDU_LINKS.map((link) => (
            <li key={link.to}>
              <a
                href={link.to}
                onClick={onClose}
                className="bp-mm-gate-edu-link"
                data-testid={`mm-gate-edu-${link.to.replace(/[/]/g, '-')}`}
                style={{ fontSize: 13, color: '#7fb8ff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {link.label}
                <ArrowRight size={12} aria-hidden="true" />
              </a>
            </li>
          ))}
          {stateAuth && (
            <li>
              <a
                href={stateAuth.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bp-mm-gate-edu-link"
                data-testid="mm-gate-edu-state-auth"
                style={{ fontSize: 13, color: '#7fb8ff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {stateAuth.label} - official state authority
                <ExternalLink size={12} aria-hidden="true" />
              </a>
            </li>
          )}
        </ul>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="bp-mm-secondary"
          onClick={onClose}
          data-testid="mm-gate-close"
          style={{
            padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 500,
            background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
            color: 'inherit', cursor: 'pointer',
          }}
        >
          Close for now
        </button>
      </div>
    </div>
  );
}
